import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Tag, Package, StickyNote, Zap, ShieldCheck, Truck, Ruler, Car, Layers } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useContact } from "../../hooks/useSettings";

const PERK_ICONS = [Zap, Tag, Truck, ShieldCheck];

const DEFAULT_OPTIONS = {
    widths: Array.from({ length: 18 }, (_, i) => String(155 + i * 10)),
    heavy_widths: ["750", "825", "900"],
    heights: Array.from({ length: 14 }, (_, i) => String(30 + i * 5)),
    rims: Array.from({ length: 14 }, (_, i) => `R${13 + i}`),
    inch_dias: Array.from({ length: 13 }, (_, i) => String(30 + i)),
    inch_widths: ["9.50", "10.50", "11.50", "12.50", "13.50", "14.50"],
    years: Array.from({ length: 12 }, (_, i) => String(2026 - i)),
};

export const TireInquiry = () => {
    const { t, tr, lang, fs } = useLang();
    const { waUrl } = useContact();
    const isAr = lang === "ar";

    const [brands, setBrands] = useState([]);
    const [types, setTypes] = useState([]);
    const [options, setOptions] = useState(DEFAULT_OPTIONS);
    const [mode, setMode] = useState("size");
    const [sys, setSys] = useState("metric");
    const [width, setWidth] = useState("");
    const [height, setHeight] = useState("");
    const [rim, setRim] = useState("");
    const [dia, setDia] = useState("");
    const [widthIn, setWidthIn] = useState("");
    const [cars, setCars] = useState(null);
    const [carBrand, setCarBrand] = useState("");
    const [carModel, setCarModel] = useState("");
    const [carTrim, setCarTrim] = useState("");
    const [carRim, setCarRim] = useState("");
    const [carYear, setCarYear] = useState("2024");
    const [tireBrand, setTireBrand] = useState("");
    const [tireType, setTireType] = useState("");
    const [qty, setQty] = useState("4");
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");

    const METRIC_WIDTHS = options.widths || DEFAULT_OPTIONS.widths;
    const HEAVY_WIDTHS = (options.heavy_widths || DEFAULT_OPTIONS.heavy_widths).map(Number);
    const HEIGHTS = options.heights || DEFAULT_OPTIONS.heights;
    const RIMS = options.rims || DEFAULT_OPTIONS.rims;
    const INCH_DIAS = options.inch_dias || DEFAULT_OPTIONS.inch_dias;
    const INCH_WIDTHS = options.inch_widths || DEFAULT_OPTIONS.inch_widths;
    const YEARS = options.years || DEFAULT_OPTIONS.years;

    useEffect(() => {
        api.get("/inquiry/brands")
            .then((r) => setBrands(r.data || []))
            .catch(() => {
                api.get("/tires/meta")
                    .then((r) => setBrands((r.data.brands || []).map((b) => ({ id: b.brand, name_en: b.brand, name_ar: b.brand_ar }))))
                    .catch(() => {});
            });
        api.get("/inquiry/types").then((r) => setTypes(r.data || [])).catch(() => {});
        api.get("/inquiry/options")
            .then((r) => setOptions({ ...DEFAULT_OPTIONS, ...r.data }))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (mode === "car" && !cars) {
            api.get("/inquiry/cars")
                .then((r) => setCars(r.data.brands || []))
                .catch(() => {
                    fetch("/data/saudi_cars.json")
                        .then((r) => r.json())
                        .then((d) => setCars(d.brands || []))
                        .catch(() => setCars([]));
                });
        }
    }, [mode, cars]);

    const isHeavy = HEAVY_WIDTHS.includes(Number(width));

    const sizeString = useMemo(() => {
        if (sys === "inch") {
            return dia && widthIn && rim ? `${dia}/${widthIn}${rim}` : "";
        }
        if (!width || !rim) return "";
        if (isHeavy) return `${width} ${rim}`;
        return height ? `${width}/${height} ${rim}` : "";
    }, [sys, dia, widthIn, rim, width, height, isHeavy]);

    const carModels = useMemo(() => {
        if (!cars || !carBrand) return [];
        return cars.find((b) => b.ar === carBrand)?.models || [];
    }, [cars, carBrand]);

    const carTrims = useMemo(() => {
        const m = carModels.find((m) => m.ar === carModel);
        return m?.trims || [];
    }, [carModels, carModel]);

    const selectCls =
        "w-full bg-[#0A1228]/70 border border-white/10 rounded-2xl py-3.5 px-4 text-sm text-white focus:outline-none focus:border-amber-400/60 transition-colors duration-300 cursor-pointer appearance-none";

    const submit = (e) => {
        e.preventDefault();
        const lines = [];
        if (isAr) {
            lines.push("السلام عليكم، أود الاستفسار عن إطارات من شركة إطار الثريا لخدمات السيارات:");
        } else {
            lines.push("Hello, I would like to inquire about tires from Al Thuraya Tire Auto Services Co:");
        }

        if (mode === "size") {
            if (!sizeString) {
                setError(t("inq_err_size"));
                return;
            }
            lines.push(`${isAr ? "المقاس" : "Size"}: ${sizeString}`);
        } else {
            if (!carBrand || !carModel || !carRim) {
                setError(t("inq_err_car"));
                return;
            }
            const b = cars.find((x) => x.ar === carBrand);
            const m = carModels.find((x) => x.ar === carModel);
            const tr_ = carTrims.find((x) => x.ar === carTrim);
            const carLine = [
                isAr ? b?.ar : b?.en,
                isAr ? m?.ar : m?.en,
                carTrim ? (isAr ? tr_?.ar : tr_?.en) : null,
            ]
                .filter(Boolean)
                .join(" — ");
            lines.push(`${isAr ? "السيارة" : "Car"}: ${carLine}`);
            lines.push(`${isAr ? "سنة الموديل" : "Model year"}: ${carYear}`);
            lines.push(`${isAr ? "مقاس الجنط" : "Rim size"}: ${carRim}`);
        }

        if (tireType) lines.push(`${isAr ? "النوعية" : "Type"}: ${tireType}`);
        if (tireBrand) lines.push(`${isAr ? "الماركة" : "Brand"}: ${tireBrand}`);
        lines.push(`${isAr ? "الكمية" : "Quantity"}: ${qty}`);
        if (notes.trim()) lines.push(`${isAr ? "ملاحظات" : "Notes"}: ${notes.trim()}`);

        setError("");
        window.open(waUrl(lines.join("\n")), "_blank");
    };

    const tabCls = (active) =>
        `flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-extrabold transition-colors duration-300 ${
            active ? "bg-amber-400 text-[#0A1228]" : "text-slate-400 hover:text-white"
        }`;

    return (
        <section id="inquiry" className="relative py-24 sm:py-32" data-testid="inquiry-section">
            <div className="absolute -top-20 -left-32 w-[420px] h-[420px] rounded-full bg-[#253C96]/30 blur-[130px]" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono-spec text-xs tracking-[0.3em] text-amber-400 mb-3" dir="ltr">
                        {t("inq_eyebrow")}
                    </p>
                    <h2 className={`font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.2] ${fs("inq_t1")}`}>
                        {t("inq_t1")}
                        <span className="block mt-1 text-transparent bg-clip-text bg-gradient-to-l from-amber-300 via-amber-400 to-yellow-600">
                            {t("inq_t2")}
                        </span>
                    </h2>
                    <p className={`mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg ${fs("inq_desc")}`}>
                        {t("inq_desc")}
                    </p>
                    <div className="mt-8 space-y-4">
                        {tr.inq_perks.map((text, i) => {
                            const Icon = PERK_ICONS[i];
                            return (
                                <motion.div
                                    key={text}
                                    initial={{ opacity: 0, x: 24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2 + i * 0.1, duration: 0.55 }}
                                    className="flex items-center gap-3"
                                >
                                    <span className="w-9 h-9 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0">
                                        <Icon className="w-4 h-4 text-amber-400" />
                                    </span>
                                    <p className={`text-sm text-slate-300 ${fs("inq_perks")}`}>{text}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="spotlight-card border border-white/10 rounded-3xl p-6 sm:p-8 space-y-5"
                    data-testid="inquiry-form"
                >
                    <h3 className={`font-display font-bold text-lg text-white ${fs("inq_form_title")}`}>{t("inq_form_title")}</h3>

                    <div className="flex bg-[#0A1228]/70 border border-white/10 rounded-full p-1">
                        <button type="button" onClick={() => setMode("size")} data-testid="inquiry-mode-size" className={tabCls(mode === "size")}>
                            <Ruler className="w-4 h-4" />
                            {t("inq_mode_size")}
                        </button>
                        <button type="button" onClick={() => setMode("car")} data-testid="inquiry-mode-car" className={tabCls(mode === "car")}>
                            <Car className="w-4 h-4" />
                            {t("inq_mode_car")}
                        </button>
                    </div>

                    {mode === "size" ? (
                        <>
                            <div className="flex bg-[#0A1228]/70 border border-white/10 rounded-full p-1">
                                <button type="button" onClick={() => setSys("metric")} data-testid="inquiry-sys-metric" className={tabCls(sys === "metric")}>
                                    {t("inq_sys_metric")}
                                </button>
                                <button type="button" onClick={() => setSys("inch")} data-testid="inquiry-sys-inch" className={tabCls(sys === "inch")}>
                                    {t("inq_sys_inch")}
                                </button>
                            </div>

                            {sys === "metric" ? (
                                <div className={`grid gap-3 ${isHeavy ? "grid-cols-2" : "grid-cols-3"}`}>
                                    <select value={width} onChange={(e) => setWidth(e.target.value)} data-testid="inquiry-width-select" className={selectCls} dir="ltr">
                                        <option value="">{t("inq_select_width")}</option>
                                        {METRIC_WIDTHS.map((w) => (
                                            <option key={w} value={w}>{w}</option>
                                        ))}
                                        <optgroup label={t("inq_heavy_group")}>
                                            {HEAVY_WIDTHS.map((w) => (
                                                <option key={w} value={w}>{w}</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                    {!isHeavy && (
                                        <select value={height} onChange={(e) => setHeight(e.target.value)} data-testid="inquiry-height-select" className={selectCls} dir="ltr">
                                            <option value="">{t("inq_select_height")}</option>
                                            {HEIGHTS.map((h) => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                    )}
                                    <select value={rim} onChange={(e) => setRim(e.target.value)} data-testid="inquiry-rim-select" className={selectCls} dir="ltr">
                                        <option value="">{t("inq_select_rim")}</option>
                                        {RIMS.map((r) => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-3">
                                    <select value={dia} onChange={(e) => setDia(e.target.value)} data-testid="inquiry-dia-select" className={selectCls} dir="ltr">
                                        <option value="">{t("inq_select_dia")}</option>
                                        {INCH_DIAS.map((d) => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                    <select value={widthIn} onChange={(e) => setWidthIn(e.target.value)} data-testid="inquiry-width-in-select" className={selectCls} dir="ltr">
                                        <option value="">{t("inq_select_width_in")}</option>
                                        {INCH_WIDTHS.map((w) => (
                                            <option key={w} value={w}>{w}</option>
                                        ))}
                                    </select>
                                    <select value={rim} onChange={(e) => setRim(e.target.value)} data-testid="inquiry-rim-select" className={selectCls} dir="ltr">
                                        <option value="">{t("inq_select_rim")}</option>
                                        {RIMS.map((r) => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div
                                className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${
                                    sizeString ? "border-amber-400/40 bg-amber-400/10" : "border-white/10 bg-white/[0.03]"
                                }`}
                                data-testid="inquiry-size-preview"
                            >
                                <span className="text-xs font-bold text-slate-400">{t("inq_size_preview")}</span>
                                <span className={`font-mono-spec font-bold text-lg ${sizeString ? "text-amber-400" : "text-slate-600"}`} dir="ltr">
                                    {sizeString || t("inq_size_missing")}
                                </span>
                            </div>
                            {sys === "metric" && isHeavy && (
                                <p className="text-[11px] text-amber-400/80 -mt-2" data-testid="inquiry-heavy-hint">
                                    {t("inq_heavy_hint")}
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <select
                                value={carBrand}
                                onChange={(e) => { setCarBrand(e.target.value); setCarModel(""); setCarTrim(""); }}
                                data-testid="inquiry-car-brand-select"
                                className={selectCls}
                            >
                                <option value="">{t("inq_car_brand")}</option>
                                {(cars || []).map((b) => (
                                    <option key={b.ar} value={b.ar}>{isAr ? b.ar : b.en}</option>
                                ))}
                            </select>
                            <select
                                value={carModel}
                                onChange={(e) => { setCarModel(e.target.value); setCarTrim(""); }}
                                disabled={!carBrand}
                                data-testid="inquiry-car-model-select"
                                className={`${selectCls} disabled:opacity-40`}
                            >
                                <option value="">{t("inq_car_model")}</option>
                                {carModels.map((m) => (
                                    <option key={m.ar} value={m.ar}>{isAr ? m.ar : m.en}</option>
                                ))}
                            </select>
                            {carTrims.length > 0 && (
                                <select
                                    value={carTrim}
                                    onChange={(e) => setCarTrim(e.target.value)}
                                    data-testid="inquiry-car-trim-select"
                                    className={selectCls}
                                >
                                    <option value="">{t("inq_car_trim")}</option>
                                    {carTrims.map((tr_) => (
                                        <option key={tr_.ar} value={tr_.ar}>{isAr ? tr_.ar : tr_.en}</option>
                                    ))}
                                </select>
                            )}
                            <select
                                value={carYear}
                                onChange={(e) => setCarYear(e.target.value)}
                                data-testid="inquiry-car-year-select"
                                className={selectCls}
                                dir="ltr"
                            >
                                {YEARS.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                            <select
                                value={carRim}
                                onChange={(e) => setCarRim(e.target.value)}
                                data-testid="inquiry-car-rim-select"
                                className={selectCls}
                                dir="ltr"
                            >
                                <option value="">{t("inq_car_rim")}</option>
                                {RIMS.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="relative">
                        <Layers className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <select
                            value={tireType}
                            onChange={(e) => setTireType(e.target.value)}
                            data-testid="inquiry-type-select"
                            className={`${selectCls} pr-11 ${tireType ? "" : "text-slate-500"}`}
                        >
                            <option value="">{t("inq_type_ph")}</option>
                            {types.map((ty) => (
                                <option key={ty.id} value={isAr ? ty.name_ar : ty.name_en}>
                                    {isAr ? ty.name_ar : ty.name_en}
                                </option>
                            ))}
                            <option value={isAr ? "أخرى" : "Other"}>{t("inq_type_other")}</option>
                        </select>
                    </div>

                    <div className="relative">
                        <Tag className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <select
                            value={tireBrand}
                            onChange={(e) => setTireBrand(e.target.value)}
                            data-testid="inquiry-brand-select"
                            className={`${selectCls} pr-11 ${tireBrand ? "" : "text-slate-500"}`}
                        >
                            <option value="">{t("inq_brand_ph")}</option>
                            {brands.map((b) => (
                                <option key={b.id || b.name_en} value={isAr ? b.name_ar : b.name_en}>
                                    {isAr ? `${b.name_ar} — ${b.name_en}` : `${b.name_en} — ${b.name_ar}`}
                                </option>
                            ))}
                            <option value={isAr ? "أخرى" : "Other"}>{t("inq_other")}</option>
                        </select>
                    </div>

                    <div className="relative">
                        <Package className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <select
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            data-testid="inquiry-qty-select"
                            className={`${selectCls} pr-11`}
                        >
                            {["1", "2", "4"].map((n) => (
                                <option key={n} value={n}>{t("inq_qty_ph")}: {n}</option>
                            ))}
                            <option value={isAr ? "أكثر من 4" : "More than 4"}>
                                {t("inq_qty_ph")}: {isAr ? "أكثر من 4" : "4+"}
                            </option>
                        </select>
                    </div>

                    <div className="relative">
                        <StickyNote className="absolute right-4 top-4 w-4 h-4 text-slate-500" />
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t("inq_notes_ph")}
                            data-testid="inquiry-notes-input"
                            className="w-full bg-[#0A1228]/70 border border-white/10 rounded-2xl py-3.5 pr-11 pl-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300 h-24 resize-none"
                        />
                    </div>

                    {error && (
                        <p className="text-xs text-red-400" data-testid="inquiry-error">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        data-testid="inquiry-whatsapp-submit"
                        className={`w-full flex items-center justify-center gap-2.5 bg-emerald-500 text-[#0A1228] font-extrabold text-base rounded-2xl py-4 hover:bg-emerald-400 transition-colors duration-300 shadow-[0_0_35px_rgba(16,185,129,0.25)] ${fs("inq_submit")}`}
                    >
                        <MessageCircle className="w-5 h-5" />
                        {t("inq_submit")}
                    </button>
                    <p className="text-[11px] text-slate-600 text-center">
                        {t("inq_disclaimer")}
                    </p>
                </motion.form>
            </div>
        </section>
    );
};
